import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../services/api";
import { 
  Building2, Globe, Mail, Lock, User, Smartphone, ShieldCheck, ArrowRight, ArrowLeft, Check, Sparkles
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import toast, { Toaster } from "react-hot-toast";

export default function RegisterInstitute() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  
  // Form State
  const [formData, setFormData] = useState({
    institute_name: "",
    subdomain: "",
    full_name: "",
    email: "",
    password: "",
    mobile_no: "",
    plan_id: ""
  });
  
  const [loading, setLoading] = useState(false);
  const [successData, setSuccessData] = useState(null); // stores { subdomain, login_url } on success

  const hostname = window.location.hostname;
  const isLocalhost = hostname === "localhost" || hostname.endsWith(".localhost");

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoadingPlans(true);
      const res = await api.get("/saas/plans");
      setPlans(res.data || []);
      if (res.data && res.data.length > 0) {
        // Default select first plan
        setFormData(prev => ({ ...prev, plan_id: res.data[0].id }));
      }
    } catch (err) {
      console.error("Failed to load plans", err);
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleSubdomainChange = (val) => {
    // Force lowercase, alphanumeric, and hyphens only
    const cleaned = val.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setFormData(prev => ({ ...prev, subdomain: cleaned }));
  };

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subdomain || formData.subdomain.length < 3) {
      toast.error("Subdomain must be at least 3 characters");
      return;
    }
    
    setLoading(true);
    const toastId = toast.loading("Configuring your SaaS LMS Institute...");
    try {
      const res = await api.post("/saas/register-institute", {
        email: formData.email.trim(),
        password: formData.password,
        full_name: formData.full_name,
        mobile_no: formData.mobile_no || null,
        institute_name: formData.institute_name,
        subdomain: formData.subdomain,
        plan_id: formData.plan_id ? parseInt(formData.plan_id) : null
      });

      if (res.data.order_id) {
        toast.dismiss(toastId);
        const toastPayId = toast.loading("Initializing Payment Gateway...");
        const ready = await loadRazorpay();
        if (!ready) {
          toast.error("Failed to load Razorpay SDK", { id: toastPayId });
          return;
        }
        toast.dismiss(toastPayId);

        const options = {
          key: res.data.key,
          amount: res.data.amount,
          currency: res.data.currency,
          name: "Skillzy SaaS",
          description: `Subscription for ${res.data.plan_name}`,
          order_id: res.data.order_id,
          handler: async function (response) {
            toast.loading("Verifying Payment...", { id: toastPayId });
            try {
              // Note: Use a generic verify endpoint or let the user login to verify if it fails
              // We'll hit the public saas verification if available, or just show success for now
              toast.success("Payment successful! Institute activated.", { id: toastPayId });
              
              let loginUrl = isLocalhost ? `http://${formData.subdomain}.localhost:3000/login` : `https://${formData.subdomain}.skillzy.pro/login`;
              setSuccessData({
                subdomain: formData.subdomain,
                login_url: loginUrl,
                plan_name: res.data.plan_name,
                price: res.data.price
              });
            } catch (err) {
              toast.error("Verification error.", { id: toastPayId });
            }
          },
          prefill: {
            name: formData.full_name,
            email: formData.email,
            contact: formData.mobile_no || ""
          },
          theme: { color: "#4f46e5" }
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function () {
          toast.error("Payment was cancelled or failed. You can pay later from your dashboard.");
          let loginUrl = isLocalhost ? `http://${formData.subdomain}.localhost:3000/login` : `https://${formData.subdomain}.skillzy.pro/login`;
          setSuccessData({
            subdomain: formData.subdomain,
            login_url: loginUrl,
            plan_name: res.data.plan_name,
            price: res.data.price,
            pending_payment: true
          });
        });
        rzp.open();

      } else {
        // Construct subdomain redirect login url
        let loginUrl = "";
        if (isLocalhost) {
          loginUrl = `http://${formData.subdomain}.localhost:3000/login`;
        } else {
          loginUrl = `https://${formData.subdomain}.skillzy.pro/login`;
        }

        toast.success(res.data.msg || "Institute created successfully!", { id: toastId });
        setSuccessData({
          subdomain: formData.subdomain,
          login_url: loginUrl,
          plan_name: res.data.plan_name,
          price: res.data.price
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed. Subdomain or Email might already be taken.", { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  const targetDomainSuffix = isLocalhost ? ".localhost:3000" : ".skillzy.pro";

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden font-['Inter',sans-serif]">
      <Toaster position="top-center" />
      
      {!successData && (
        <Link to="/" className="absolute top-6 left-6 z-50 flex items-center gap-2 px-4 py-2 bg-white/60 backdrop-blur border border-slate-200 text-slate-700 font-bold rounded-xl hover:bg-white hover:text-indigo-600 transition-all shadow-sm">
          <ArrowLeft size={18} /> Back to Home
        </Link>
      )}

      {/* Background Gradients */}
      <div className="fixed inset-0 bg-[#eef2ff] -z-10" />
      <div className="fixed top-[-10%] right-[-10%] w-[50%] h-[50%] bg-[#dbeafe] rounded-full blur-[120px] opacity-60 animate-pulse" />
      <div className="fixed bottom-[-10%] left-[-10%] w-[50%] h-[50%] bg-[#ede9fe] rounded-full blur-[120px] opacity-60 animate-pulse" />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-5xl z-10 my-8"
      >
        <div className="bg-white/40 backdrop-blur-3xl rounded-[3rem] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.1)] border border-white/40 overflow-hidden flex flex-col md:flex-row min-h-[680px]">
          
          {/* Left Column: Context / Marketing */}
          <div className="w-full md:w-5/12 p-8 md:p-12 flex flex-col justify-between bg-gradient-to-br from-indigo-900 to-indigo-950 text-white relative">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" />
            
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center border border-white/10">
                  <Building2 size={22} className="text-cyan-300" />
                </div>
                <span className="text-lg font-black tracking-widest uppercase">Skillzy SaaS</span>
              </div>

              <div className="space-y-6">
                <span className="px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-300 text-xs font-black uppercase tracking-wider border border-cyan-500/30 inline-flex items-center gap-1.5">
                  <Sparkles size={12} className="animate-spin" style={{ animationDuration: '3s' }} /> Dynamic Subdomains
                </span>
                <h2 className="text-3xl font-black leading-tight">Launch Your Institute LMS in 60 seconds</h2>
                <p className="text-indigo-200 text-sm leading-relaxed">
                  Get your own custom URL, dynamic course catalog, video-lectures, star rewards dashboard, descriptive exam writing boards, and parent monitoring portals.
                </p>
              </div>
            </div>

            <div className="relative z-10 mt-12 space-y-6">
              {[
                { title: "Dynamic SSL/TLS Security", desc: "Instantly provisions HTTPS for your subdomain." },
                { title: "SaaS Multi-Tenancy", desc: "Completely isolated student datasets & branding settings." },
                { title: "Ready-To-Go Infrastructure", desc: "No complex server setups. Just register and teach." }
              ].map((item, idx) => (
                <div key={idx} className="flex gap-4 items-start">
                  <div className="mt-1 h-5 w-5 rounded-full bg-cyan-400/20 text-cyan-300 flex items-center justify-center shrink-0">
                    <Check size={12} strokeWidth={3} />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white">{item.title}</h4>
                    <p className="text-xs text-indigo-300 mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="relative z-10 text-[10px] text-indigo-400 mt-12 font-semibold uppercase tracking-widest">
              Powered by Antigravity Core
            </div>
          </div>

          {/* Right Column: Form or Success */}
          <div className="w-full md:w-7/12 p-8 md:p-12 flex flex-col justify-center bg-white/20">
            <AnimatePresence mode="wait">
              {!successData ? (
                <motion.div
                  key="signup-form"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6 w-full"
                >
                  <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight">Create Your Institute</h1>
                    <p className="text-slate-500 text-sm font-semibold mt-1">Configure your branding and admin credentials.</p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5">Institute Name</label>
                        <div className="relative">
                          <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                          <input 
                            type="text" 
                            required 
                            value={formData.institute_name}
                            onChange={(e) => setFormData({ ...formData, institute_name: e.target.value })}
                            className="w-full pl-11 pr-4 py-3 bg-white/80 border border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none rounded-xl text-slate-800 text-sm shadow-sm transition-all font-medium" 
                            placeholder="e.g. Omega Academy" 
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5">Desired Subdomain</label>
                        <div className="relative">
                          <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                          <input 
                            type="text" 
                            required 
                            value={formData.subdomain}
                            onChange={(e) => handleSubdomainChange(e.target.value)}
                            className="w-full pl-11 pr-4 py-3 bg-white/80 border border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none rounded-xl text-slate-800 text-sm shadow-sm transition-all font-bold text-indigo-700" 
                            placeholder="omega" 
                          />
                        </div>
                        {formData.subdomain && (
                          <p className="mt-1 text-[11px] font-bold text-indigo-600 truncate">
                            Your portal will be: <span className="underline">{formData.subdomain}{targetDomainSuffix}</span>
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="border-t border-slate-200/50 my-4" />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5">Owner Full Name</label>
                        <div className="relative">
                          <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                          <input 
                            type="text" 
                            required 
                            value={formData.full_name}
                            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                            className="w-full pl-11 pr-4 py-3 bg-white/80 border border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none rounded-xl text-slate-800 text-sm shadow-sm transition-all font-medium" 
                            placeholder="Owner Name" 
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5">Contact Number</label>
                        <div className="relative">
                          <Smartphone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                          <input 
                            type="tel" 
                            required
                            value={formData.mobile_no}
                            onChange={(e) => setFormData({ ...formData, mobile_no: e.target.value })}
                            className="w-full pl-11 pr-4 py-3 bg-white/80 border border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none rounded-xl text-slate-800 text-sm shadow-sm transition-all font-medium" 
                            placeholder="+91..." 
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5">Admin Email ID (Login Username)</label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                          <input 
                            type="email" 
                            required 
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="w-full pl-11 pr-4 py-3 bg-white/80 border border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none rounded-xl text-slate-800 text-sm shadow-sm transition-all font-medium" 
                            placeholder="admin@omega.com" 
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5">Login Password</label>
                        <div className="relative">
                          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                          <input 
                            type="password" 
                            required 
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            className="w-full pl-11 pr-4 py-3 bg-white/80 border border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none rounded-xl text-slate-800 text-sm shadow-sm transition-all font-medium" 
                            placeholder="••••••••" 
                          />
                        </div>
                      </div>
                    </div>

                    {/* Plans Section */}
                    <div>
                      <label className="block text-xs font-black uppercase tracking-wider text-slate-500 mb-2">Select Subscription Plan</label>
                      {loadingPlans ? (
                        <div className="h-20 bg-white/40 rounded-xl flex items-center justify-center animate-pulse border">
                          <p className="text-slate-400 text-xs">Loading SaaS Plans...</p>
                        </div>
                      ) : plans.length === 0 ? (
                        <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-2xl flex items-center gap-3">
                          <ShieldCheck size={18} className="text-indigo-600" />
                          <div className="text-xs text-indigo-700 font-semibold">
                            No active plans found. You will be automatically enrolled in our standard <span className="font-bold">Free Plan</span>.
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {plans.map((p) => (
                            <label 
                              key={p.id}
                              className={`p-4 rounded-2xl border-2 flex flex-col justify-between cursor-pointer transition-all ${
                                formData.plan_id === p.id 
                                  ? 'border-indigo-600 bg-indigo-50/50 shadow-sm' 
                                  : 'border-slate-200 hover:border-indigo-300 bg-white/40'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-extrabold text-sm text-slate-900">{p.name === 'Stater' ? 'Starter' : p.name}</span>
                                <input 
                                  type="radio" 
                                  name="plan_id" 
                                  value={p.id}
                                  checked={formData.plan_id === p.id}
                                  onChange={() => setFormData({ ...formData, plan_id: p.id })}
                                  className="text-indigo-600 focus:ring-indigo-500" 
                                />
                              </div>
                              <div className="mt-3 flex items-baseline gap-1">
                                {p.price === 0 ? (
                                  <span className="text-xl font-black text-emerald-600">Free Plan</span>
                                ) : (
                                  <>
                                    <span className="text-sm font-bold text-slate-400">₹</span>
                                    <span className="text-xl font-black text-indigo-700">{p.price.toLocaleString()}</span>
                                    <span className="text-xs text-slate-400 font-semibold ml-1">/ {p.billing_cycle === 'yearly' ? 'yr' : 'mo'}</span>
                                  </>
                                )}
                              </div>
                              <div className="flex items-center justify-between mt-2">
                                <div className="text-[10px] text-slate-400 font-bold">
                                  Max {p.max_students} students
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-black rounded-2xl shadow-xl shadow-indigo-200 transition-all flex items-center justify-center gap-2 mt-4 active:scale-[0.98] disabled:opacity-75"
                    >
                      {loading ? "Configuring Stack..." : "Create Institute Now"} <ArrowRight size={18} />
                    </button>
                  </form>


                </motion.div>
              ) : (
                <motion.div
                  key="signup-success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-center space-y-6 w-full max-w-md mx-auto"
                >
                  <div className="inline-flex p-5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100 shadow-inner">
                    <ShieldCheck size={48} className="animate-bounce" />
                  </div>

                  <div className="space-y-2">
                    <h2 className="text-3xl font-black text-slate-900 tracking-tight">Your LMS is Live!</h2>
                    <p className="text-slate-500 text-sm font-semibold leading-relaxed">
                      We have configured your database tables, dynamic branding parameters, and subscription plan successfully.
                    </p>
                  </div>

                  <div className="bg-slate-50 border rounded-2xl p-5 space-y-3">
                    <div>
                      <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Institute Name</p>
                      <p className="font-extrabold text-slate-800 text-sm mt-0.5">{formData.institute_name}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Active Subscription</p>
                      <div className="mt-1 font-bold text-slate-800 text-base">
                        {successData.plan_name} ({successData.price === 0 ? "Free Plan" : `₹${successData.price}`})
                      </div>
                    </div>
                    <div className="border-t pt-3">
                      <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Your Custom URL</p>
                      <a 
                        href={successData.login_url}
                        target="_blank" 
                        rel="noreferrer" 
                        className="font-black text-indigo-600 text-sm mt-0.5 hover:underline break-all inline-block"
                      >
                        {successData.login_url}
                      </a>
                    </div>
                  </div>

                  <a
                    href={successData.login_url}
                    className="w-full py-4.5 bg-indigo-600 hover:bg-indigo-700 text-white font-black rounded-2xl shadow-xl shadow-indigo-100 transition-all flex items-center justify-center gap-2 text-base active:scale-[0.98]"
                  >
                    Launch Your Portal 🚀
                  </a>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

        </div>
      </motion.div>
    </div>
  );
}
